import axios, { AxiosError } from 'axios';
import api from './baseApi';

export const T_TEAM_KIND = 't_team';
export const DEFAULT_TEAM_GROUP_NAME = 'T-lag';

export type TeamGroupKind = typeof T_TEAM_KIND;

export const TEAM_GROUP_ERROR_CODES = {
  UNSUPPORTED_KIND: 'TEAM_GROUP_UNSUPPORTED_KIND',
  DUPLICATE_TEAM_ID: 'TEAM_GROUP_DUPLICATE_TEAM_ID',
  DUPLICATE_MEMBER: 'TEAM_GROUP_DUPLICATE_MEMBER',
  INVALID_MEMBER: 'TEAM_GROUP_INVALID_MEMBER',
} as const;

export type TeamGroupErrorCode =
  (typeof TEAM_GROUP_ERROR_CODES)[keyof typeof TEAM_GROUP_ERROR_CODES];

export type TeamGroupTeamDto = {
  _id: string;
  name: string;
  memberIds: string[];
};

export type TeamGroupDto = {
  _id: string;
  tribe_id: string;
  kind: TeamGroupKind;
  name: string;
  status: 'active';
  teams: TeamGroupTeamDto[];
  previousTeams: TeamGroupTeamDto[];
  createdAt: string;
  updatedAt: string;
};

export type TeamGroupTeamInput = {
  _id?: string;
  name: string;
  memberIds: string[];
};

export type PutTeamGroupRequest = {
  name?: string;
  teams: TeamGroupTeamInput[];
};

export type TeamGroupApiErrorBody = {
  code?: string;
  errorMessage?: string;
  message?: string;
};

export type ParsedTeamGroupError = {
  status?: number;
  code?: string;
  message: string;
};

const LOAD_FALLBACK = 'Could not load teams. Please try again.';
const SAVE_FALLBACK = 'Something went wrong while saving. Please try again.';

const CODE_FALLBACKS: Record<string, string> = {
  [TEAM_GROUP_ERROR_CODES.UNSUPPORTED_KIND]: 'This team type is not supported.',
  [TEAM_GROUP_ERROR_CODES.DUPLICATE_TEAM_ID]: 'The same team was sent twice.',
  [TEAM_GROUP_ERROR_CODES.DUPLICATE_MEMBER]:
    'Each person can only be on one team.',
  [TEAM_GROUP_ERROR_CODES.INVALID_MEMBER]:
    'A selected person is not an active circle member.',
};

const readErrorBody = (error: AxiosError): TeamGroupApiErrorBody => {
  const data = error.response?.data;
  if (data && typeof data === 'object') {
    return data as TeamGroupApiErrorBody;
  }
  return {};
};

export const parseTeamGroupError = (
  error: unknown,
  operation: 'get' | 'put'
): ParsedTeamGroupError => {
  const fallback = operation === 'get' ? LOAD_FALLBACK : SAVE_FALLBACK;

  if (!axios.isAxiosError(error)) {
    return { message: fallback };
  }

  const status = error.response?.status;
  const body = readErrorBody(error);
  const code = body.code;
  const serverText = body.errorMessage || body.message;

  if (status === 500 || status == null) {
    return { status, code, message: fallback };
  }

  if (status === 403) {
    return {
      status,
      code,
      message:
        serverText ||
        (operation === 'put'
          ? 'You must be a leader or co-leader to save teams.'
          : 'You must be a circle member to view teams.'),
    };
  }

  if (status === 404) {
    return {
      status,
      code,
      message: serverText || 'This circle could not be found.',
    };
  }

  if (status === 400) {
    return {
      status,
      code,
      message:
        serverText ||
        (code ? CODE_FALLBACKS[code] : undefined) ||
        'Please check the team details and try again.',
    };
  }

  return { status, code, message: serverText || fallback };
};

export const teamGroupsApi = {
  async getTeamGroup(
    circleId: string,
    kind: TeamGroupKind = T_TEAM_KIND
  ): Promise<TeamGroupDto | null> {
    const response = await api.get(`tribes/${circleId}/team-groups/${kind}`);
    return response.data.result ?? null;
  },

  async putTeamGroup(
    circleId: string,
    body: PutTeamGroupRequest,
    kind: TeamGroupKind = T_TEAM_KIND
  ): Promise<TeamGroupDto> {
    const response = await api.put(
      `tribes/${circleId}/team-groups/${kind}`,
      body
    );
    return response.data.result;
  },
};
